import React, { useState } from 'react';
import { Card, ListGroup, Badge, Row, Col, Alert, Modal, Button, Spinner } from 'react-bootstrap';
import { createBooking } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const BookingSummary = ({ 
  selectedServices, 
  selectedBarber, 
  selectedDate, 
  selectedTime, 
  customerData,
  corporationId 
}) => {
  // Calculate total price
  const totalPrice = selectedServices.reduce((sum, service) => {
    return sum + parseFloat(service.price || 0);
  }, 0);

  // Calculate total duration
  const totalDuration = selectedServices.reduce((sum, service) => {
    return sum + (service.duration || 0);
  }, 0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleConfirmBooking = async () => {
    // Validate required fields
    if (!customerData.name || !customerData.phone) {
      setError('Name and phone number are required');
      return;
    }

    if (!selectedBarber || !selectedBarber.id) {
      setError('Please select a professional');
      return;
    }

    if (!selectedDate || !selectedTime) {
      setError('Please select a date and time');
      return;
    }

    if (!selectedServices || selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }

    if (!corporationId) {
      setError('Corporation ID is missing');
      return;
    }

    // Prepare booking data
    const bookingData = {
      corporation_id: corporationId,
      name: customerData.name,
      phone: customerData.phone,
      email: customerData.email || undefined, // Optional field
      staff_id: selectedBarber.id,
      booking_date: selectedDate,
      start_time: selectedTime,
      services: selectedServices.map(service => service.id),
      notes: customerData.notes || undefined // Optional field
    };

    try {
      setLoading(true);
      setError(null);
      
      const response = await createBooking(bookingData);
      
      if (response.success && response.data?.booking) {
        setBookingSuccess(response.data.booking);
        setShowSuccessModal(true);
      } else {
        setError(response.message || 'Failed to create booking');
      }
    } catch (err) {
      console.error('Booking error:', err);
      let errorMessage = 'Failed to create booking. Please try again.';
      
      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.errors) {
          // Handle validation errors
          const errors = err.response.data.errors;
          if (typeof errors === 'object') {
            const errorMessages = Object.entries(errors)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('; ');
            errorMessage = errorMessages;
          } else {
            errorMessage = String(errors);
          }
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-summary">
      <h6 className="mb-3">Review Your Booking</h6>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          <Alert.Heading>Booking Error</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}

      {loading && (
        <div className="text-center my-4">
          <LoadingSpinner />
          <p className="mt-3">Processing your booking...</p>
        </div>
      )}
      
      <Row className="g-4">
        <Col md={8}>
          <Card className="mb-3">
            <Card.Header className="bg-light border-bottom">
              <h6 className="mb-0 fw-normal text-muted" style={{ fontSize: '0.8125rem' }}>Services</h6>
            </Card.Header>
            <ListGroup variant="flush">
              {selectedServices.length > 0 ? (
                selectedServices.map((service) => (
                  <ListGroup.Item key={service.id} className="py-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="small">
                        <div className="fw-medium">{service.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {service.duration} min
                        </div>
                      </div>
                      <div className="fw-medium small">
                        RM{service.price}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item className="py-2">
                  <span className="text-muted small">No services selected</span>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>

          <Card className="mb-3">
            <Card.Header className="bg-light border-bottom">
              <h6 className="mb-0 fw-normal text-muted" style={{ fontSize: '0.8125rem' }}>Booking Details</h6>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item className="py-2">
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Professional:</span>
                  <span className="small">
                    {selectedBarber ? (
                      selectedBarber.id === 'any' ? (
                        <span>Any Available Staff</span>
                      ) : (
                        <>{selectedBarber.name} {selectedBarber.position && <span className="text-muted">- {selectedBarber.position}</span>}</>
                      )
                    ) : (
                      <span className="text-muted">Not selected</span>
                    )}
                  </span>
                </div>
              </ListGroup.Item>
              
              <ListGroup.Item className="py-2">
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Date & Time:</span>
                  <span className="small">
                    {selectedDate && selectedTime ? (
                      <>{selectedDate} at {selectedTime}</>
                    ) : (
                      <span className="text-muted">Not selected</span>
                    )}
                  </span>
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>

          <Card>
            <Card.Header className="bg-light border-bottom">
              <h6 className="mb-0 fw-normal text-muted" style={{ fontSize: '0.8125rem' }}>Your Information</h6>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item className="py-2">
                <div className="small">
                  <span className="text-muted">Name:</span> {customerData.name || <span className="text-muted">Not provided</span>}
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="py-2">
                <div className="small">
                  <span className="text-muted">Phone:</span> {customerData.phone || <span className="text-muted">Not provided</span>}
                </div>
              </ListGroup.Item>
              {customerData.email && (
                <ListGroup.Item className="py-2">
                  <div className="small">
                    <span className="text-muted">Email:</span> {customerData.email}
                  </div>
                </ListGroup.Item>
              )}
              {customerData.notes && (
                <ListGroup.Item className="py-2">
                  <div className="small">
                    <span className="text-muted">Notes:</span> {customerData.notes}
                  </div>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>
        </Col>

        <Col md={4}>
          <div className="sticky-top" style={{ top: '20px' }}>
            <Card className="mb-3">
              <Card.Body className="p-3">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">Services ({selectedServices.length}):</span>
                    <span>RM{totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">Duration:</span>
                    <span>{totalDuration} min</span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <strong className="small">Total:</strong>
                    <strong className="fs-6" style={{ color: '#212529' }}>RM{totalPrice.toFixed(2)}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
            
            <Button
              variant="success"
              size="lg"
              className="w-100"
              onClick={handleConfirmBooking}
              disabled={loading || !customerData.name || !customerData.phone || !selectedBarber || !selectedDate || !selectedTime || selectedServices.length === 0}
              style={{ 
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Confirm Booking
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => window.location.reload()} size="lg" centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-check-circle me-2"></i>
            Booking Confirmed!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bookingSuccess && (
            <div>
              <Alert variant="success" className="mb-4">
                <Alert.Heading>Your booking has been confirmed!</Alert.Heading>
                <p className="mb-0">Booking Number: <strong>{bookingSuccess.booking_number}</strong></p>
              </Alert>

              {bookingSuccess.services && bookingSuccess.services.length > 0 && (
                <Card className="mb-3">
                  <Card.Header className="bg-light">
                    <strong>Services</strong>
                  </Card.Header>
                  <ListGroup variant="flush">
                    {bookingSuccess.services.map((service) => {
                      // Try to get service name from API response, or match with selectedServices
                      const serviceName = service.name || 
                        selectedServices.find(s => s.id === service.id)?.name || 
                        'Service';
                      return (
                        <ListGroup.Item key={service.id || service.service_id}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-medium">{serviceName}</div>
                              <div className="text-muted small">
                                {service.duration || selectedServices.find(s => s.id === service.id)?.duration || 0} min
                              </div>
                            </div>
                            <div className="fw-medium">
                              RM{service.price || selectedServices.find(s => s.id === service.id)?.price || 0}
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </Card>
              )}

              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <strong>Booking Details</strong>
                </Card.Header>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>Date:</strong> {bookingSuccess.booking_date} at {bookingSuccess.start_time}
                    {bookingSuccess.end_time && ` - ${bookingSuccess.end_time}`}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Professional:</strong> {
                      bookingSuccess.staff?.name || 
                      (selectedBarber?.id === 'any' ? 'Any Available Staff (Assigned by system)' : selectedBarber?.name)
                    }
                    {bookingSuccess.staff?.position && ` - ${bookingSuccess.staff.position}`}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Total Price:</strong> <span className="fw-bold">RM{bookingSuccess.total_price}</span>
                  </ListGroup.Item>
                </ListGroup>
              </Card>

              {bookingSuccess.notes && (
                <Alert variant="info" className="mt-3">
                  <strong>Your Notes:</strong> {bookingSuccess.notes}
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => {
            setShowSuccessModal(false);
            // Optionally reload the page or reset the form
            window.location.reload();
          }}>
            <i className="bi bi-check me-2"></i>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BookingSummary;
